class NewsletterPopup extends HTMLElement {
  constructor() {
    super();
    this.popup = this.querySelector("div");
    this.closeButton = this.querySelector("#close-newsletter");
    this.init();
  }

  init() {
    if (localStorage.getItem("newsletterDismissed")) {
      this.popup.removeAttribute("open");
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get("customer_posted") === "true") {
      this.popup.removeAttribute("open");
      localStorage.setItem("newsletterDismissed", "true");
      return;
    }

    setTimeout(() => {
      this.popup.setAttribute("open", "true");
    }, 500);

    this.closeButton.addEventListener("click", () => {
      this.popup.removeAttribute("open");
      setTimeout(() => {
        localStorage.setItem("newsletterDismissed", "true");
      }, 500);
    });
  }
}

customElements.define("newsletter-popup", NewsletterPopup);

class AccordionElement extends HTMLElement {
  constructor() {
    super();
    this.details = this.closest("details");
    this.summary = this.details.querySelector("summary");

    this._resetHeight();

    this.onClick = this.onClick.bind(this);
    this.onResize = this.onResize.bind(this);

    this.summary.addEventListener("click", this.onClick);
    window.addEventListener("resize", this.onResize);
  }

  _open() {
    this.details.setAttribute("data-opening", "true");
    this.details.setAttribute("open", "true");
    this.details.style.height = `${this.summary.scrollHeight + this.scrollHeight}px`;
  }

  _resetHeight() {
    this.details.style.height = `${this.summary.scrollHeight}px`;
  }

  _close() {
    this.details.removeAttribute("data-opening");
    this.details.setAttribute("data-closing", "true");

    setTimeout(() => {
      this.details.removeAttribute("open");
      this.details.removeAttribute("data-closing");
    }, 300);

    this.details.style.height = `${this.summary.scrollHeight}px`;
  }

  onClick(e) {
    e.preventDefault();
    if (!this.details.open) {
      this._open();
    } else {
      this._close();
    }
  }

  onResize() {
    if (this.details.open) {
      this.details.style.height = `${this.summary.scrollHeight + this.scrollHeight}px`;
    } else {
      this._resetHeight();
    }
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.onResize);
  }
}

if (!customElements.get("accordion-element")) {
  customElements.define("accordion-element", AccordionElement);
}

class QuickAddToCart extends HTMLElement {
  constructor() {
    super();
    this.variantId = this.getAttribute("data-variant-id");
    this.onClick = this.onClick.bind(this);
    this.addEventListener("click", this.onClick);
  }

  async onClick(e) {
    e.preventDefault();
    
    // Optimistically open cart UI
    const cartDrawer = document.getElementById("cart");
    if (cartDrawer) {
      cartDrawer.classList.add("open");
      cartDrawer.style.opacity = "1";
      cartDrawer.style.pointerEvents = "auto";
    }

    try {
      await fetch("/cart/add.js", {
        method: "post",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: this.variantId,
          quantity: 1,
        }),
      });

      // Delegate the HTML update to the safer DOMParser script at the bottom
      document.dispatchEvent(new Event("cart:quick-add"));
    } catch (error) {
      console.error(error);
    }
  }
}

if (!customElements.get("quick-add")) {
  customElements.define("quick-add", QuickAddToCart);
}

class SizeChart extends HTMLElement {
  constructor() {
    super();

    this.sizeChart = JSON.parse(this.getAttribute("data-size-chart") || "[]");
    const shadow = this.attachShadow({ mode: "open" });

    const style = document.createElement("style");
    style.textContent = `
      * {
      font-family: "Arial", sans-serif;
      font-style: normal;
      font-weight: 400;
      font-size: 10px;
      line-height: 13px;
      text-align: left;
      }
      .table-header, .table-header th { text-transform: uppercase; }
      table { width: 100%; outline: 0; border-collapse: collapse; }
      th, td { width: 1%; }
      th { padding-bottom: 7.5px; }
      td { padding: 7.5px 0; }
      tr:last-child td { padding-bottom: 0; }
      .break { height: 0.5px; background-color: #D1D1D1; }
    `;

    const table = this.generateTable();
    shadow.appendChild(style);
    shadow.appendChild(table);
  }

  generateTable() {
    const table = document.createElement("table");

    if (this.sizeChart.length > 0) {
      const headers = Object.values(this.sizeChart[0]);
      const thead = document.createElement("thead");
      thead.classList.add("table-header");

      const headerRow = document.createElement("tr");
      headers.forEach((headerText) => {
        const th = document.createElement("th");
        th.textContent = headerText;
        headerRow.appendChild(th);
      });

      thead.appendChild(headerRow);
      table.appendChild(thead);

      const tbody = document.createElement("tbody");
      this.sizeChart.slice(1).forEach((rowData) => {
        const row = document.createElement("tr");

        Object.values(rowData).forEach((cellData) => {
          const cell = document.createElement("td");
          cell.textContent = cellData;
          row.appendChild(cell);
        });

        tbody.appendChild(row);
      });
      table.appendChild(tbody);
    }

    return table;
  }
}

if (!customElements.get("size-chart")) {
  customElements.define("size-chart", SizeChart);
}

class CartItem extends HTMLElement {
  constructor() {
    super();
    this.decreaseButton = this.querySelector("button[id='decrease']");
    this.increaseButton = this.querySelector("button[id='increase']");
    this.pid = this.getAttribute("data-product-id");
  }
}

if (!customElements.get("cart-item")) {
  customElements.define("cart-item", CartItem);
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.openDrawer = this.openDrawer.bind(this);
    this.closeDrawer = this.closeDrawer.bind(this);
    this.addEventListener("click", this.onClick);
  }

  onClick(e) {
    const isCloseButton = e.target.closest("#close");
    if (e.target.id === "cart" || isCloseButton) {
      e.preventDefault();
      if (window.location.pathname === "/cart") {
        window.location.href = "/";
      } else {
        this.closeDrawer();
      }
    }
  }

  openDrawer() {
    this.setAttribute("open", "true");
    const header = document.querySelector("header-holder");
    if (header && typeof header.closeMenu === "function") {
      header.closeMenu();
    }
  }

  closeDrawer() {
    this.removeAttribute("open");
  }

  async updateQuantity(itemId, quantity) {
    await fetch("/cart/change.js", {
      method: "post",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: itemId, quantity: quantity }),
    });
    await this.update();
  }

  async update() {
    try {
      const response = await fetch("/cart");
      const text = await response.text();
      const parser = new DOMParser();
      const newDocument = parser.parseFromString(text, "text/html");
      const cartElement = newDocument.getElementById("cart");
      
      if (cartElement) {
        const cartDrawer = document.getElementById("cart");
        cartDrawer.innerHTML = cartElement.innerHTML;
        document.dispatchEvent(new Event("cart:updated"));
      }
    } catch (error) {
      console.error("Cart update failed:", error);
    }
  }

  async addToCart(form) {
    await fetch("/cart/add.js", {
      method: "post",
      body: new FormData(form),
    });
    await this.update().then(() => {
      this.openDrawer();
      document.dispatchEvent(new Event("cart:updated"));
    });
  }
}

if (!customElements.get("cart-drawer")) {
  customElements.define("cart-drawer", CartDrawer);
}

class AddToCart extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector("button[id='atc']");
    this.quickCheckout = this.querySelector("button[id='quick-checkout']");
    
    // NOTE: Removed the direct listener here so it doesn't conflict with 
    // the safer global Add To Cart listener at the bottom of the page!

    this.variants = this.querySelectorAll("div[id='variant']");
    this.setInitialVariant();
    this.activeVariant = this.activeVariant.bind(this);
    this.variants.forEach((variant) => {
      variant.addEventListener("click", (e) => {
        this.activeVariant(e);
      });
    });
  }

  setInitialVariant() {
    let activeVariant = this.querySelector("input[id='selected-variant-id']");
    this.variants.forEach((variant) => {
      if (variant.getAttribute("data-variant-id") === activeVariant.value) {
        variant.classList.add("selected");
      }
    });
  }

  async activeVariant(e) {
    let activeVariant = this.querySelector("input[id='selected-variant-id']");
    this.variants.forEach((variant) => {
      variant.classList.remove("selected");
    });
    e.target.classList.add("selected");
    activeVariant.value = e.target.getAttribute("data-variant-id");
  }

  async onClickQuickCheckout(e) {
    e.preventDefault();
    let activeVariant = document.querySelector("input[name='id']").value;
    let checkoutLink = `/cart/${activeVariant}:1`;
    window.open(checkoutLink, "_self");
  }
}

if (!customElements.get("cart-add")) {
  customElements.define("cart-add", AddToCart);
}

class HeaderCart extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.addEventListener("click", this.onClick);
  }

  onClick(e) {
    e.preventDefault();
    document.querySelector("cart-drawer").openDrawer();
  }
}

if (!customElements.get("header-cart")) {
  customElements.define("header-cart", HeaderCart);
}

class RemoveButton extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.addEventListener("click", this.onClick);
  }

  async onClick(e) {
    e.preventDefault();

    const form = this.closest("form");

    await fetch(e.target.href, {
      method: "post",
      body: new FormData(form),
    });

    const response = await fetch("/cart.js");
    const cartData = await response.json();

    if (cartData.item_count === 0) {
      document.querySelector("cart-drawer").closeDrawer();
    }

    await document.querySelector("cart-drawer").update();
  }
}

if (!customElements.get("remove-button")) {
  customElements.define("remove-button", RemoveButton);
}

class Newsletter extends HTMLElement {
  constructor() {
    super();
    this.email = this.querySelector("input[id='email']");
    this.button = this.querySelector("button[id='subscribe']");
    this.onClick = this.onClick.bind(this);

    const urlParams = new URLSearchParams(window.location.search);
    const customerPosted = urlParams.get("customer_posted");
    if (customerPosted === "true") {
      this.changeStatus("Thank you for subscribing");
    }
    this.button.addEventListener("click", this.onClick);
  }
  async onClick(e) {
    if (this.email.value.length < 5) {
      this.changeStatus("Please enter a valid email ");
    }
  }

  async changeStatus(text) {
    this.querySelector("p").textContent = text;
  }
}

if (!customElements.get("newsletter")) {
  customElements.define("newsletter-widget", Newsletter);
}

class SlideShow extends HTMLElement {
  constructor() {
    super();
    this.initSwiper();
  }

  initSwiper() {
    const swiperContainer = this;
    if (swiperContainer) {
      const swiper = new Swiper(".swiper", {
        slidesPerView: 1,
        spaceBetween: 0,
        speed: 400,
        on: {
          init: this.updateProgressBar.bind(this),
          slideChange: this.updateProgressBar.bind(this),
        },
      });
    }
  }
  updateProgressBar() {
    this.querySelector("div[id='progress']").style.width = `${
      ((this.swiper.activeIndex + 1) / this.swiper.slides.length) * 100
    }%`;
  }
}

if (!customElements.get("product-slideshow")) {
  customElements.define("product-slideshow", SlideShow);
}

class SearchWidget extends HTMLElement {
  constructor() {
    super();
    this.searchPlaceholder = this.querySelector("#search-placeholder");
    this.searchInput = this.querySelector("#search");

    this.toggleSearch = this.toggleSearch.bind(this);
    this.executeSearch = this.executeSearch.bind(this);

    this.searchPlaceholder.addEventListener("click", this.toggleSearch);
    this.searchInput.addEventListener("keydown", this.executeSearch);
  }

  toggleSearch() {
    this.searchPlaceholder.classList.add("hidden");
    this.searchInput.classList.remove("hidden");
    this.searchInput.focus();
  }

  executeSearch(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      const query = this.searchInput.value.trim();

      if (query.length > 0) {
        window.location.href = `/search?q=${encodeURIComponent(
          query,
        )}&type=product`;
      }
    }
  }
}

if (!customElements.get("search-widget")) {
  customElements.define("search-widget", SearchWidget);
}

class Header extends HTMLElement {
  constructor() {
    super();
    this.opened = false;
    this.menuButton = this.querySelector("#mobile-menu-btn");

    if (this.menuButton) {
      this.onClick = this.onClick.bind(this);
      this.menuButton.addEventListener("click", this.onClick);
    }
  }

  onClick(e) {
    e.preventDefault();
    this.opened ? this.closeMenu() : this.openMenu();
  }

  openMenu() {
    this.opened = true;
    document.getElementById("mobile-menu").style.transform = "translateX(0%)";
    document.body.style.overflow = "hidden";
  }

  closeMenu() {
    this.opened = false;
    document.getElementById("mobile-menu").style.transform = "translateX(100%)";
    document.body.style.overflow = "";
  }
}

if (!customElements.get("header-holder")) {
  customElements.define("header-holder", Header);
}

document.addEventListener("DOMContentLoaded", function () {
  const cartBoxes = document.querySelectorAll("#cart-box");
  const cartCounts = document.querySelectorAll("#cart-count");
  const cartDrawer = document.getElementById("cart");
  const addToCartButton = document.getElementById("atc");
  const productForm = document.querySelector("form[action='/cart/add']");
  const closeButton = document.getElementById("close");
  const mobileMenu = document.getElementById("mobile-menu");
  const mobileMenuClose = document.getElementById("mobile-menu-close");

  async function updateCartCount(forceOpen = false) {
    try {
      const response = await fetch("/cart.js");
      const data = await response.json();

      cartCounts.forEach(cartCount => {
        cartCount.textContent = data.item_count > 0 ? `[${data.item_count}]` : "[0]";
      });

      if (forceOpen && cartDrawer) {
        cartDrawer.style.opacity = "1";
        cartDrawer.style.pointerEvents = "auto";
      }
    } catch (error) {
      console.error("Error updating cart count:", error);
    }
  }

  async function updateCartQuantity(variantId, newQuantity) {
    try {
        const cartDrawer = document.getElementById("cart");

        cartDrawer.classList.add("open");
        cartDrawer.style.opacity = "1";
        cartDrawer.style.pointerEvents = "auto";

        const response = await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: variantId, quantity: newQuantity }),
        });

        if (response.ok) {
            updateCartDrawer();
            document.dispatchEvent(new Event("cart:updated")); 
        } else {
            console.error("Failed to update cart quantity");
        }

    } catch (error) {
        console.error("Error updating cart quantity:", error);
    }
  }

  async function updateCartDrawer() {
    try {
        const cartDrawer = document.getElementById("cart");

        cartDrawer.classList.add("open");
        cartDrawer.style.opacity = "1";
        cartDrawer.style.pointerEvents = "auto";

        const response = await fetch("/cart");
        const text = await response.text();
        const parser = new DOMParser();
        const newDocument = parser.parseFromString(text, "text/html");
        
        const newCartElement = newDocument.getElementById("cart");
        if (newCartElement) {
          cartDrawer.innerHTML = newCartElement.innerHTML; 
        }

        cartDrawer.classList.add("open");
        cartDrawer.style.opacity = "1";
        cartDrawer.style.pointerEvents = "auto";

        attachCartEventListeners(); 

    } catch (error) {
        console.error("Error updating cart drawer:", error);
    }
  }

  function attachCartEventListeners() {
    document.querySelectorAll(".quantity-btn").forEach(button => {
      button.addEventListener("click", function (event) {
        event.preventDefault(); 

        const variantId = this.getAttribute("data-id");
        const action = this.getAttribute("data-action");
        const quantityElement = document.querySelector(`.cart-quantity[data-id="${variantId}"]`);
        let currentQuantity = parseInt(quantityElement.textContent, 10);

        if (action === "increase") {
          updateCartQuantity(variantId, currentQuantity + 1);
        } else if (action === "decrease") {
          updateCartQuantity(variantId, Math.max(currentQuantity - 1, 0));
        }
      });
    });

    document.querySelectorAll(".remove-button a").forEach(removeButton => {
      removeButton.addEventListener("click", async function (event) {
        event.preventDefault();
        try {
          await fetch(this.href, { method: "POST" });
          updateCartDrawer(); 
        } catch (error) {
          console.error("Error removing item from cart:", error);
        }
      });
    });
  }

  updateCartCount(); 
  attachCartEventListeners(); 

  // **RESTORED: Safer Add To Cart Logic**
  if (addToCartButton && productForm) {
    addToCartButton.addEventListener("click", async function (event) {
      event.preventDefault();
      
      // Open drawer immediately so it feels snappy
      if (cartDrawer) {
        cartDrawer.classList.add("open");
        cartDrawer.style.opacity = "1";
        cartDrawer.style.pointerEvents = "auto";
      }

      const formData = new FormData(productForm);

      try {
        await fetch("/cart/add.js", { method: "POST", body: formData });
        await updateCartDrawer(); // Safely fetches the new cart HTML
        updateCartCount(true);
      } catch (error) {
        console.error("Error adding to cart:", error);
      }
    });
  }

  if (cartDrawer) {
    cartDrawer.addEventListener("click", function (event) {
      const button = event.target.closest(".quantity-btn");
      if (!button) return;

      event.preventDefault(); 

      const variantId = button.getAttribute("data-id");
      const action = button.getAttribute("data-action");
      const quantityElement = document.querySelector(`.cart-quantity[data-id="${variantId}"]`);
      let currentQuantity = parseInt(quantityElement.textContent, 10);

      if (action === "increase") {
        updateCartQuantity(variantId, currentQuantity + 1);
      } else if (action === "decrease") {
        updateCartQuantity(variantId, Math.max(currentQuantity - 1, 0));
      }
    });
  }

  cartBoxes.forEach(cartBox => {
    cartBox.addEventListener("click", function () {
      if (cartDrawer) {
        cartDrawer.style.opacity = "1";
        cartDrawer.style.pointerEvents = "auto";
      }
      if (mobileMenu && mobileMenu.style.transform === "translateX(0%)") {
        mobileMenu.style.transform = "translateX(100%)";
        document.body.style.overflow = "";
      }
    });
  });

  if (closeButton) {
    closeButton.addEventListener("click", function () {
      cartDrawer.style.opacity = "0";
      cartDrawer.style.pointerEvents = "none";
    });
  }

  if (mobileMenuClose) {
    mobileMenuClose.addEventListener("click", function () {
      mobileMenu.style.transform = "translateX(100%)";
      document.body.style.overflow = "";
    });
  }

  document.addEventListener("cart:updated", () => updateCartCount());
  
  // Listen for our new safe Quick Add event
  document.addEventListener("cart:quick-add", async () => {
    await updateCartDrawer();
    updateCartCount(true);
  });

  document.addEventListener("shopify:section:load", () => updateCartCount());
});
// =============================================
// NOTIFY ME WIDGET
// =============================================
class NotifyMe extends HTMLElement {
  constructor() {
    super();
    this.klaviyoKey = this.getAttribute('data-klaviyo-key') || '';
    this.listId     = this.getAttribute('data-list-id')     || '';
    this.smsListId  = this.getAttribute('data-sms-list-id') || '';
    this.variantId  = this.getAttribute('data-variant-id')  || '';
    this.button     = this.querySelector('.notify-btn');
    this.form       = this.querySelector('.notify-form');
    this.input      = this.querySelector('.notify-input');
    this.submit     = this.querySelector('.notify-submit');
    this.status     = this.querySelector('.notify-status');

    if (this.button) this.button.addEventListener('click', () => this._showForm());
    if (this.submit) this.submit.addEventListener('click', () => this._handleSubmit());
    if (this.input)  this.input.addEventListener('keydown', (e) => { if (e.key === 'Enter') this._handleSubmit(); });
  }

  _showForm() {
    this.button.style.display = 'none';
    this.form.style.display   = 'block';
    this.input.focus();
  }

  _isValidEmail(e) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e); }

  // Normalize phone to E.164 (+1XXXXXXXXXX for US numbers)
  _formatPhone(raw) {
    const digits = raw.replace(/\D/g, '');
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    return null; // invalid
  }

  _showStatus(text, type) {
    this.status.textContent   = text;
    this.status.className     = `notify-status ${type}`;
    this.status.style.display = 'block';
  }

  async _handleSubmit() {
    // Re-read attrs in case JS updated them after construction
    this.variantId  = this.getAttribute('data-variant-id')  || this.variantId;
    this.klaviyoKey = this.getAttribute('data-klaviyo-key') || this.klaviyoKey;
    this.listId     = this.getAttribute('data-list-id')     || this.listId;
    this.smsListId  = this.getAttribute('data-sms-list-id') || this.smsListId;

    const email       = this.input.value.trim();
    const phoneInput  = this.querySelector('.notify-phone');
    const smsCheck    = this.querySelector('.notify-sms-check');
    const optinCheck  = this.querySelector('.notify-optin-check');
    const rawPhone    = phoneInput ? phoneInput.value.trim() : '';
    const wantsSMS    = smsCheck && smsCheck.checked;

    if (!email || !this._isValidEmail(email)) { this._showStatus('ENTER A VALID EMAIL', 'error'); return; }
    if (!this.klaviyoKey) { this._showStatus('KLAVIYO KEY MISSING — CHECK THEME SETTINGS', 'error'); return; }

    // Validate phone if SMS opted in
    let formattedPhone = null;
    if (wantsSMS && rawPhone) {
      formattedPhone = this._formatPhone(rawPhone);
      if (!formattedPhone) { this._showStatus('ENTER A VALID US PHONE NUMBER', 'error'); return; }
    } else if (wantsSMS && !rawPhone) {
      this._showStatus('ENTER YOUR PHONE NUMBER FOR TEXT ALERTS', 'error'); return;
    }

    this.submit.disabled    = true;
    this.submit.textContent = '...';

    const headers = { 'Content-Type': 'application/json', 'revision': '2023-12-15' };
    const calls   = [];

    // 1. Back-in-stock notification (always, via email)
    if (this.variantId) {
      calls.push(fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.klaviyoKey}`, {
        method: 'POST', headers,
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: { data: { type: 'profile', attributes: { email } } }
            },
            relationships: {
              variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${this.variantId}` } }
            }
          }
        })
      }));
    }

    // 2. Email newsletter opt-in
    if (this.listId && optinCheck && optinCheck.checked) {
      calls.push(fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${this.klaviyoKey}`, {
        method: 'POST', headers,
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: { profile: { data: { type: 'profile', attributes: { email } } } },
            relationships: { list: { data: { type: 'list', id: this.listId } } }
          }
        })
      }));
    }

    // 3. SMS opt-in
    if (this.smsListId && wantsSMS && formattedPhone) {
      calls.push(fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${this.klaviyoKey}`, {
        method: 'POST', headers,
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              channels: { sms: ['sms'] },
              profile: { data: { type: 'profile', attributes: { email, phone_number: formattedPhone } } }
            },
            relationships: { list: { data: { type: 'list', id: this.smsListId } } }
          }
        })
      }));
    }

    try {
      await Promise.allSettled(calls);
      this.form.style.display = 'none';
      this._showStatus("YOU\'RE ON THE LIST.", 'success');
    } catch (err) {
      console.error('NotifyMe error:', err);
      this._showStatus('SOMETHING WENT WRONG. TRY AGAIN.', 'error');
      this.submit.disabled    = false;
      this.submit.textContent = 'SUBMIT';
    }
  }
}

if (!customElements.get('notify-me')) {
  customElements.define('notify-me', NotifyMe);
}
